import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { CheckCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import {
    Button,
    CardContent,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

import { QueryRendererActions } from '@barboxx/admin-components';
import Api from '@barboxx/api';
import { BusinessStripeAccount } from '@barboxx/models';
import { Can } from '@barboxx/react-utils';

import { REACT_APP_ORIGIN } from '../../../../../../environments/environment';

interface Props {
    stripeAccount: BusinessStripeAccount;
    businessId: number;
    refetch: QueryRendererActions['refetch'];
}

function LineItem({ name, value }: { name: string; value: boolean }) {
    return (
        <ListItem>
            <ListItemText>{name}</ListItemText>
            <ListItemIcon>
                {value ? (
                    <CheckCircleOutline color="success" />
                ) : (
                    <RemoveCircleOutline color="error" />
                )}
            </ListItemIcon>
        </ListItem>
    );
}

export function FinancialContent({ stripeAccount, businessId }: Props) {
    const linkAccountMutation = useMutation({
        mutationKey: ['createAccount', businessId],
        mutationFn: () =>
            Api.Stripe.linkConnectedAccount({
                accountId: stripeAccount.stripeAccountId,
                origin: REACT_APP_ORIGIN,
                businessId,
            }),
        onSuccess: ({ data, ok }) => {
            if (ok && data) {
                const { url } = data;
                if (url) {
                    window.location.assign(url);
                }
            }
        },
        onError: (error, variables, context) => {
            console.error('Error linking account:', error);
        },
    });

    const handleLinkAccount = () => {
        linkAccountMutation.mutate();
    };

    const BODY_CONTENT = React.useMemo(
        () =>
            stripeAccount.detailsSubmitted
                ? 'In order to continue receiving business payouts it is important to keep your bank and payment information up to date and accurate at all times.'
                : 'BarBoxx partners with Stripe and uses Stripe Payments to process all payments and business payouts. Stripe keeps all your account information private and secure',
        [stripeAccount.detailsSubmitted]
    );

    return (
        <CardContent>
            <Stack gap={2}>
                <Typography variant="body1">{BODY_CONTENT}</Typography>

                <LineItem
                    name="Details submitted"
                    value={stripeAccount.detailsSubmitted}
                />
                <LineItem
                    name="Charges enabled"
                    value={stripeAccount.chargesEnabled}
                />
                <LineItem
                    name="Payout enabled"
                    value={stripeAccount.payoutsEnabled}
                />
                <Can I="create" an="BusinessStripeAccount">
                    <Button
                        loading={linkAccountMutation.isPending}
                        onClick={handleLinkAccount}
                        variant="contained"
                    >
                        {stripeAccount.detailsSubmitted
                            ? 'Update Information'
                            : 'Add Information'}
                    </Button>
                </Can>

                {linkAccountMutation.error && (
                    <Typography variant="body1" color="error">
                        Something went wrong!
                    </Typography>
                )}
            </Stack>
        </CardContent>
    );
}
